<template>
  <div class="orderAsideNew">
    <div class="top p-16 md:p-24">
      <div class="flex border-b border-grey-light pb-16">
        <div class="hotel-image" v-lazy:background-image="this['order/order'].hotel.coverImage || require('@/assets/images/default-room.jpg')">
        </div>
        <div class="ml-16 py-4">
          <div class="text-base font-medium">{{ this['order/order'].hotel.hotelName }}</div>
          <div class="text-sm text-grey-darker mt-8">{{ this['order/order'].hotel.address }}</div>
        </div>
      </div>
      <div class="order-date py-16 text-sm border-b border-grey-light">
        <div class="font-medium">{{ $t('date') }}</div>
        <div class="flex mt-8" style="gap: 8px">
          <div class="font-medium" style="max-width: 60px;">{{ $t('checkIn.time') }}</div>
          <div style="max-width: 140px;">{{ $options.filters.toDate(this['order/order'].startAt, 'LL ddd') }}</div>
          <div>
            <span>{{ this['order/order'].hotel.check.checkInStartTimeAt }}</span>
            <span> ~ </span>
            <span>{{ this['order/order'].hotel.check.checkInEndTimeAt }}</span>
            <el-tooltip v-if="this['order/order'].hotel.check.isAllowEarlyCheckIn" effect="dark" placement="bottom">
              <i class="text-grey-dark owl-status-circle-info"></i>
              <div slot="content" class="leading-tight" style="max-width: 320px;">
                <span>{{ $t('checkIn.earlyFee') }}</span>
                <span v-if="isNumber(allowEarlyCheckInPricePerHour)">{{ currencySign }} {{ allowEarlyCheckInPricePerHour | currencyConvert({inputCurrency: this['order/order'].hotel.currency}) }}</span>
                <span v-else>{{ allowEarlyCheckInPricePerHour }}</span>
                <span v-if="isNumber(allowEarlyCheckInPricePerHour)">/{{ $t('unit.perHour') }}</span>
              </div>
            </el-tooltip>
          </div>
        </div>
        <div class="flex mt-8" style="gap: 8px">
          <div class="font-medium" style="max-width: 60px;">{{ $t('checkOut.time') }}</div>
          <div style="max-width: 140px;">{{ $options.filters.toDate(this['order/order'].endAt, 'LL ddd') }}</div>
          <div>
            <span>{{ this['order/order'].hotel.check.checkOutTimeAt }}</span>
            <el-tooltip v-if="this['order/order'].hotel.check.isAllowLateCheckOut" effect="dark" placement="bottom">
              <i class="text-grey-dark owl-status-circle-info"></i>
              <div slot="content" class="leading-tight" style="max-width: 320px;">
                <span>{{ $t('checkOut.latelyFee') }}</span>
                <span v-if="isNumber(allowLateCheckOutPricePerHour)">{{ currencySign }} {{ allowLateCheckOutPricePerHour | currencyConvert({inputCurrency: this['order/order'].hotel.currency}) }}</span>
                <span v-else>{{ allowLateCheckOutPricePerHour }}</span>
                <span v-if="isNumber(allowLateCheckOutPricePerHour)">/{{ $t('unit.perHour') }}</span>
              </div>
            </el-tooltip>
          </div>
        </div>
      </div>
      <div class="py-16 text-sm border-b border-grey-light">
        <div class="font-medium">{{ $t('peopleLimit') }}</div>
        <div class="mt-8">
          <span>{{ $t('adultCount', { count: this['order/order'].adultCount }) }}, {{ $t('childCount', { count: this['order/order'].childAges.length }) }}</span>
        </div>
        <div v-if="isMoreThanAdultLimit && isMoreThanAdultLimit > 0" class="flex items-center justify-between bg-grey-lightest mt-8 py-8 px-16" style="border-radius: 20px">
          <div class="flex items-center">
            <i class="text-base owl-status-triangle-warning text-red mr-4" />
            <div class="text-xs text-red font-medium">
              {{ this.$t('peopleLimitWarning.needToPlan', {num: isMoreThanAdultLimit}) }}
            </div>
          </div>
          <router-link :to="goToHotel('123')" class="text-xs font-bold text-red underline cursor-pointer">
            {{ $t('reselect') }}
          </router-link>
        </div>
      </div>
      <div class="py-16 text-sm border-b border-grey-light">
        <div class="font-medium">{{ $t('priceInclude') }}</div>
        <div class="flex items-center justify-between mt-8">
          <div>{{ $t('roomTotal') }}</div>
          <div>
            {{ currencySign }} {{ unTaxRoomTotal | currencyConvert({inputCurrency: hotelCurrency}) }}
          </div>
        </div>
        <div v-if="this['order/order'].extraAddonPrice" class="flex items-center justify-between mt-8">
          <div>
            <span>{{ $t('extraAddon.total') }}</span>
          </div>
          <div>
            <span v-if="isCurrencyConvert">{{ $t('priceAbout') }} </span>
            {{ currencySign }} {{ addonTotal | currencyConvert({inputCurrency: hotelCurrency}) }}
          </div>
        </div>
        <div class="flex items-center justify-between mt-8">
          <div>
            <span>{{ $t('taxesAndServiceFee') }}</span>
            <el-tooltip effect="dark" placement="bottom">
              <i class="text-grey-dark owl-status-circle-info"></i>
              <div slot="content" class="leading-tight" style="max-width: 320px;">
                {{ $t('taxesDescription') }}
              </div>
            </el-tooltip>
          </div>
          <div>
            <span v-if="isCurrencyConvert">{{ $t('priceAbout') }} </span>
            {{ currencySign }} {{ taxTotal | currencyConvert({ inputCurrency: hotelCurrency }) }}
          </div>
        </div>
        <div v-if="isAddBedDisplay" class="flex items-center justify-between mt-8">
          <div>
            <span>{{ $t('addBedService') }}</span>
          </div>
          <div>
            <span v-if="isCurrencyConvert">{{ $t('priceAbout') }} </span>
            {{ currencySign }} {{ bedTotal | currencyConvert({inputCurrency: hotelCurrency}) }}
          </div>
        </div>
        <div v-if="(this['order/premiumCoupon'] || this['order/selectedCoupon']) && this['order/isDiscount']">
          <div v-if="this['order/premiumCoupon']" class="flex items-center justify-between mt-8">
            <span>{{ this['order/premiumCoupon'].name }}</span>
            <span v-if="this['order/premiumCoupon'].discountUnit === 'price'" class="text-green-dark"><span v-if="isCurrencyConvert" class=" text-green-dark">{{ $t('priceAbout') }} </span>- {{ currencySign }} {{ Number(this['order/premiumCoupon'].discountValue) | currencyConvert({inputCurrency: hotelCurrency}) }}</span>
            <span v-if="this['order/premiumCoupon'].discountUnit === 'percent'">({{ Number(this['order/premiumCoupon'].discountValue) | toPercentage }}%) <span class="text-green-dark"><span v-if="isCurrencyConvert" class="">{{ $t('priceAbout') }} </span>-{{ currencySign }}{{ this['order/order'].premiumDiscount | currencyConvert({inputCurrency: hotelCurrency}) }}</span></span>
          </div>
          <div v-if="this['order/selectedCoupon']" class="flex items-center justify-between mt-8">
            <span>{{ this['order/selectedCoupon'].name }}</span>
            <span v-if="this['order/selectedCoupon'].discountUnit === 'price'" class="text-green-dark"><span v-if="isCurrencyConvert" class=" text-green-dark">{{ $t('priceAbout') }} </span>- {{ currencySign }} {{ Number(this['order/selectedCoupon'].discountValue) | currencyConvert({inputCurrency: hotelCurrency}) }}</span>
            <span v-if="this['order/selectedCoupon'].discountUnit === 'percent'">({{ Number(this['order/selectedCoupon'].discountValue) | toPercentage }}%) <span class="text-green-dark"><span v-if="isCurrencyConvert" class="">{{ $t('priceAbout') }} </span>-{{ currencySign }}{{ this['order/order'].couponDiscount | currencyConvert({inputCurrency: hotelCurrency}) }}</span></span>
          </div>
        </div>
      </div>
      <div class="pt-16 text-base">
        <div class="flex items-center justify-between font-bold">
          <div>{{ $t('orderTotalToPay') }}</div>
          <div>
            <span v-if="isCurrencyConvert">{{ $t('priceAbout') }} </span>{{ currencySign }} {{ orderTotalPrice | currencyConvert({inputCurrency: hotelCurrency, listInteger: true}) }} <small class='text-sm'>({{ $t('includeTax') }})</small>
          </div>
        </div>
        <div>
          <template v-if="isCurrencyConvert">
            <div class="mt-4 text-grey-dark text-sm font-bold text-left sm:text-right">({{ hotelCurrency | currencySign }} {{ orderTotalPrice | toCurrency }})</div>
            <div class="text-xs mt-8 flex items-center bg-grey-lightest px-16 py-8" style="border-radius: 20px;">
              <i class="text-orange owl-status-triangle-warning text-base"></i>
              {{ $t('displayedPriceWarning') }}
            </div>
          </template>
        </div>
      </div>
    </div>
    <div v-if="this['order/order'].cancelPolicy.lastFreeCancelDate && !hasAddon" class="bottom p-16 md:p-24 mt-24">
      <div class="font-medium text-base">{{ $t('freeToCancel.title') }}</div>
      <div class="text-sm mt-16 leading-normal">
        {{ $t('freeToCancel.desp', { date: $options.filters.toDate(this['order/order'].cancelPolicy.lastFreeCancelDate, 'LL') }) }}
      </div>
      <div class="mt-16 flex items-center justify-between" style="gap: 8px;">
        <i class="el-icon-success text-blue-light"></i>
        <div class="bg-blue-light w-full" style="border-radius: 5px; height: 2px;"></div>
        <div class="custom-circle"></div>
        <div class="bg-grey-light w-full" style="border-radius: 5px; height: 2px;"></div>
        <div class="custom-circle"></div>
      </div>
      <div class="mt-8 flex items-center justify-between text-xs">
        <span class="text-left">{{ $t('freeToCancel.today') }}</span>
        <span class="ml-16">{{ $options.filters.toDate(this['order/order'].cancelPolicy.lastFreeCancelDate, 'LL') }}</span>
        <span>{{ $t('freeToCancel.checkInDay') }}</span>
      </div>
    </div>
    <div v-if="isMultipleCancelPolicy && !hasAddon" class="bottom p-16 md:p-24 mt-24">
      <div class="font-medium text-base"><i class="text-red owl-status-triangle-warning text-base"></i>{{ $t('strictCancelPolicy.title') }}</div>
      <div class="text-sm mt-16 leading-normal">
        {{ $t('strictCancelPolicy.desp', { type: this['order/order'].cancelPolicy.title }) }}
        <span class="text-blue-light font-medium cursor-pointer" @click="openPlanCancelPolicy">{{ $t('strictCancelPolicy.checkPolicy') }}</span>
      </div>
    </div>
  </div>
</template>
<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import fullPageModal from '@/components/modals/fullPageModal'
import cancelPolicy from '@/components/cancelPolicyNew'

export default {
  name: 'OrderAsideNew',
  data () {
    return {
      includeTip: null,
      excludeTip: null,
      isCreatingOrder: false
    }
  },
  filters: {
    toPercentage: function (val) {
      return 100 - val * 100
    }
  },
  computed: {
    ...mapGetters([
      'isMobile',
      'order/sortedCart',
      'order/order',
      'search/dateRange',
      'user/currencySign',
      'user/selectedCurrency',
      'user/isPremium',
      'order/isDiscount',
      'order/discountTotal',
      'order/selectedCoupon',
      'order/premiumCoupon',
      'hotels/externalHotelSource'
    ]),
    isAddBedDisplay: function () {
      let count = 0
      for (const room of this['order/order'].rooms) {
        for (const plan of room.plans) {
          for (const type in plan.bed) {
            count += plan.bed[type]
          }
        }
      }
      return count > 0
    },
    bedTotal: function () {
      let count = 0
      if (this['order/order'].hotel.extraBedRules) {
        let price = {
          adult: this['order/order'].hotel.extraBedRules.adult.isCharge ? this['order/order'].hotel.extraBedRules.adult.price : 0,
          child: this['order/order'].hotel.extraBedRules.child.isCharge ? this['order/order'].hotel.extraBedRules.child.price : 0,
          infant: 0
        }
        for (const room of this['order/order'].rooms) {
          for (const plan of room.plans) {
            for (const type in plan.bed) {
              count += plan.bed[type] * price[type] * this.totalNights
            }
          }
        }
      }
      return count
    },
    peopleTotal: function () {
      return {
        total: [this['order/order'].adultCount, this['order/order'].childAges.length].reduce((x, y) => Number(x) + Number(y)),
        child: Number(this['order/order'].childAges.length) || 0
      }
    },
    addPeopleCount: function () {
      const array = _.flatten(this['order/order'].rooms)
      const plans = _.flatten(array.reduce((acc, curr) => {
        acc.push(curr.plans)
        return acc
      }, []))
      return _.sumBy(plans, function(plan) {
        return Number(plan.bed?.adult)
      })
    },
    isMoreThanAdultLimit: function () {
      const rooms = _.flatten(this['order/order'].rooms)
      const plans = _.flatten(rooms.reduce((acc, curr) => {
        curr.plans.forEach(e => {
          acc.push({ ...e, maxPeople: curr.maxPeople })
        })
        return acc
      }, []))
      const limit = plans.reduce((acc, curr) => {
        return acc + (curr.maxPeople * curr.count)
      }, 0)
      if ((this.peopleTotal.total - this.peopleTotal.child) > limit) {
        return (this.peopleTotal.total - this.peopleTotal.child) - limit - this.addPeopleCount
      } else {
        return false
      }
    },
    isCurrencyConvert: function () {
      return this['order/order'].currency !== this['user/selectedCurrency']
    },
    hotelCurrency: function () {
      return this['order/order'].currency
    },
    currencySign: function () {
      return this['user/currencySign']
    },
    totalNights: function () {
      const start = this.$dayjs(this['order/order'].startAt)
      const end = this.$dayjs(this['order/order'].endAt)
      return end.diff(start, 'days')
    },
    taxTotal: function () {
      return Number(this['order/order'].vat) + Number(this['order/order'].fee)
    },
    unTaxRoomTotal: function () {
      return Number(this['order/order'].untaxedPrice)
    },
    addonTotal: function () {
      return Number(this['order/order'].extraAddonPrice)
    },
    orderTotalPrice: function () {
      return Number(this['order/order'].total)
    },
    hasAddon () {
      return this['order/order'].extraAddons.length
    },
    allowEarlyCheckInPricePerHour: function () {
      if (Number(this['order/order'].hotel.check.allowEarlyCheckInPricePerHour) > 0) {
        return this['order/order'].hotel.check.allowEarlyCheckInPricePerHour
      } else {
        // return this.$t('free')
        return 0
      }
    },
    allowLateCheckOutPricePerHour: function () {
      if (Number(this['order/order'].hotel.check.allowLateCheckOutPricePerHour) > 0) {
        return this['order/order'].hotel.check.allowLateCheckOutPricePerHour
      } else {
        // return this.$t('free')
        return 0
      }
    },
    allPlans: function () {
      const array = _.flatten(this['order/order'].rooms)
      return _.flatten(array.reduce((acc, curr) => {
        acc.push(curr.plans)
        return acc
      }, []))
    },
    isMultipleCancelPolicy: function () {
      const result = _.groupBy(this.allPlans, item => {
        return item.cancelPolicy.title
      })
      return result && _.size(result) > 1
    }
  },
  methods: {
    ...mapActions([
      'order/onDeleteCartItem',
      'order/onSubmitOrder',
      'order/onSubmitOrderToBid'
    ]),
    onStartOrderingBid: function () {
      window.dataLayer.push({
        event: 'order_mysterycue'
      })

      if (this.isCreatingOrder) {
        return false
      }
      this.startOrderingBid(true)
    },
    startOrderingBid: function (bid) {
      this.isCreatingOrder = true
      this['order/onSubmitOrderToBid']({
        bid: bid,
        order: this['order/order']
      }).then(res => {
        // this.safeOpenWindow(routeData.href, '_blank')
        if (!this.isMobile) {
          const routeData = this.$router.resolve({ name: 'Callback', query: { orderSerial: res.orderSerial, orderToken: res.orderToken } })
          window.open(routeData.href, '_blank')
        } else {
          this.$router.push({
            name: 'Callback',
            query: {
              orderSerial: res.orderSerial,
              orderToken: res.orderToken
            }
          })
        }
      })
        .catch(err => {
          const keepRoomsResponseCodes = [11036, 80002, 81008, 81001, 81002] // 特定的 response status 不須顯示重新選擇日期的提示

          if (!keepRoomsResponseCodes.includes(err)) {
            setTimeout(() => {
            // this['hotels/SET_ROOMS']([])
              this.$modal.hide('dialog')
            }, 3000)
          }
        }).finally(() => {
          this.isCreatingOrder = false
        })
    },
    safeOpenWindow (url, target = '_blank') {
      const newWnd = window.open()
      if (newWnd) {
        newWnd.opener = null
        newWnd.location = url
      } else {
        const aLink = document.createElement('a')
        aLink.setAttribute('target', target)
        aLink.setAttribute('href', url)
        aLink.setAttribute('rel', 'nofollow noopener noreferrer')
        document.body.appendChild(aLink)
        aLink.click()
        aLink.remove()
      }
    },
    openPlanCancelPolicy: function () {
      window.dataLayer.push({
        event: 'order_cancellation'
      })
      this.$modal.show(fullPageModal, {
        // 輸入內容
        context: cancelPolicy,
        data: this['order/order'].cancelPolicy,
        from: 'orderDetail',
        title: this.$t('cancelPolicy')
      },
      {
        adaptive: true,
        name: 'cancelPolicy',
        // clickToClose: false,
        height: '90%',
        width: '90%',
        maxWidth: 811,
        minWidth: 345,
        maxHeight: 650,
        scrollable: true
      })
    },
    goToHotel: function () {
      return {
        name: 'HotelDetail',
        params: { hotelId: this['order/order'].hotel.hotelUUID },
        query: {
          start: this['order/order'].startAt,
          end: this['order/order'].endAt,
          adult: this['order/order'].adultCount,
          child: this['order/order'].childAges?.length ? this['order/order'].childAges?.length : null,
          ages: this['order/order'].childAges?.length ? this['order/order'].childAges?.toString() : null,
          externalId: this['order/order'].hotel.hotelUUID,
          searchId: this['order/order'].hotel.hotelUUID,
          search: this['order/order'].hotel.hotelName,
          searchType: 'hotel',
          source: this['order/order'].hotel.source
        }
      }
    },
    isNumber: function (value) {
      return !isNaN(value)
    }
  },
  mounted () {
  },
  watch: {
  }
}
</script>

<style lang="sass" scoped>
  .orderAsideNew
    @apply sticky
    top: 100px
    .top
      // @apply shadow-md rounded overflow-hidden
      @apply border-1 border-grey-lighter
      border-radius: 20px
      .hotel-image
        // padding-top: imageRatio(80, 0)
        @apply bg-center bg-cover relative overflow-hidden
        border-radius: 20px
        width: 80px
        height: 80px
        background-color: #f7f7f7
    .bottom
      @apply border-1 border-grey-lighter
      border-radius: 20px
      // @apply bg-orange-lightest border-1 border-solid border-orange-dark
      // @apply py-16 px-24 rounded mt-16
      .custom-circle
        @apply rounded-full bg-grey-light relative flex justify-center items-center
        min-width: 16px
        min-height: 16px
        width: 16px
        height: 16px
        &::after
          content: ""
          display: block
          width: 8px
          height: 8px
          background-color: #fff
          border-radius: 9999px

  .aside__block
    &--sub
      @apply border-t-1 border-grey-lightest
      // @apply px-24 py-16
      @apply flex flex-col
    &--item-row
      @apply text-sm text-grey-darker leading-md flex flex-col
      @screen sm
        @apply flex-row justify-between
      &:not(:last-of-type)
        @apply mb-16
        @screen sm
          @apply mb-0
      .discount-display
        @apply max-w-full
        @screen sm
          max-width: 50%
  .aside__info
    @apply mt-16
  .aside__title
    @apply text-sm text-white
  .aside__context
    @apply mt-8 text-white
    @apply flex justify-start items-center
    i
      @apply ml-8 text-grey-darker
    &--favor
      @apply text-red
    &--tax
      @apply text-grey-dark

  .aside__payment
    // @apply flex justify-between items-start
  .aside__payment-count
    @apply text-xl
    @apply text-blue

  .aside__agreement
    label
      @apply items-start

  .aside__hotel
    @apply flex justify-between items-start
  .aside__hotel-count
    @apply text-grey-dark
  .tippy
    @apply text-grey-dark text-sm mb-8
    i
      @apply text-grey-dark text-sm
</style>
